/**
 * Database Type Mappings
 * 
 * This file provides type mappings and utilities for converting between
 * database types (from database.generated.ts) and application types.
 */

import type { Database } from './database.generated';
import type { Json } from './database.generated';

// Extract base types from database
export type Tables = Database['public']['Tables'];
export type Functions = Database['public']['Functions'];

// Base table row types
export type DBFlashcard = Tables['flashcards']['Row'];
export type DBCollection = Tables['collections']['Row'];
export type DBSubject = Tables['subjects']['Row'];
export type DBCase = Tables['cases']['Row'];
export type DBCaseCollection = Tables['case_collections']['Row'];
export type DBCaseCollectionAnalysis = Tables['case_collection_analyses']['Row'];
export type DBCaseBriefAnalysis = Tables['case_brief_analyses']['Row'];
export type DBCourse = Tables['courses']['Row'];
export type DBModule = Tables['modules']['Row'];
export type DBLesson = Tables['lessons']['Row'];
export type DBProfile = Tables['profiles']['Row'];
export type DBThread = Tables['threads']['Row'];
export type DBMessage = Tables['messages']['Row'];
export type DBStripePriceMapping = Tables['stripe_price_mappings']['Row'];

// Insert types for creating new records
export type DBFlashcardInsert = Tables['flashcards']['Insert'];
export type DBCollectionInsert = Tables['collections']['Insert'];
export type DBCaseInsert = Tables['cases']['Insert'];
export type DBCourseInsert = Tables['courses']['Insert'];
export type DBModuleInsert = Tables['modules']['Insert'];
export type DBLessonInsert = Tables['lessons']['Insert'];

// Update types for modifying records
export type DBFlashcardUpdate = Tables['flashcards']['Update'];
export type DBCollectionUpdate = Tables['collections']['Update'];
export type DBCaseUpdate = Tables['cases']['Update'];
export type DBCourseUpdate = Tables['courses']['Update'];
export type DBModuleUpdate = Tables['modules']['Update'];
export type DBLessonUpdate = Tables['lessons']['Update'];

// Status enums from database
export type CourseStatus = Tables['courses']['Row']['status'];
export type LessonStatus = Tables['lessons']['Row']['status'];
export type ModuleStatus = 'Draft' | 'Coming Soon' | 'Published' | 'Archived'; // Assuming same as course/lesson

// Helper type for JSON fields
export type JsonPrimitive = string | number | boolean | null;
export type JsonObject = { [key: string]: Json };
export type JsonArray = Json[];

// Type guards for JSON fields
export function isJsonArray(value: Json): value is JsonArray {
  return Array.isArray(value);
}

export function isJsonObject(value: Json): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isJsonPrimitive(value: Json): value is JsonPrimitive {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

// Safely parse JSON fields with type checking
export function parseJsonArray<T>(value: Json | undefined | null, defaultValue: T[] = []): T[] {
  if (!value) return defaultValue;
  if (isJsonArray(value)) {
    return value as T[];
  }
  return defaultValue;
}

export function parseJsonObject<T extends Record<string, any>>(
  value: Json | undefined | null,
  defaultValue: T
): T {
  if (!value) return defaultValue;
  if (isJsonObject(value)) {
    return value as T;
  }
  return defaultValue;
}

// Application-friendly type mappings
export interface AppFlashcard extends DBFlashcard {
  // Add user_id for backward compatibility (maps to created_by)
  user_id: string | null;
  // Parse JSON attachments if needed
  attachments?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
  }>;
  // Optional relationships
  collections?: AppCollection[];
  subjects?: AppSubject[];
  // Backward compatibility for UI that expects collection directly
  collection?: {
    id: string;
    title: string;
    subject?: {
      name: string;
      id: string;
    }
  };
  // Additional UI properties
  progress?: number;
}

export interface AppCaseCollection extends DBCaseCollection {
  // Add relationships
  case_collection_cases?: Array<{
    id: string;
    collection_id: string;
    case_id: string;
    added_at: string | null;
    added_by: string | null;
    case?: AppCase;
  }>;
}

export interface AppCaseBriefAnalysis extends Omit<DBCaseBriefAnalysis, 'case_inputs' | 'analysis_output'> {
  // Parse JSON fields
  case_inputs: Array<{ case_name: string; citation?: string }>;
  analysis_output?: any;
  // Add backward compatibility
  collection_id?: string;
}

export interface AppCollection extends DBCollection {
  // Parse subjects from many-to-many relationship
  subjects?: AppSubject[];
  // Add flashcard count
  flashcards_count?: number;
}

export interface AppSubject extends DBSubject {
  // No additional fields needed
}

export interface AppCase extends Omit<DBCase, 'tags' | 'educational_content' | 'enrichment_metadata' | 'import_metadata' | 'html_formats' | 'analysis_metadata' | 'nicknames' | 'related_cases'> {
  // Parse JSON fields
  tags: string[];
  nicknames: string[];
  related_cases: string[];
  educational_content?: any;
  enrichment_metadata?: any;
  import_metadata?: any;
  html_formats?: any;
  analysis_metadata?: any;
  // Additional UI/Search properties
  score?: number; // For search result scoring
  matchedWords?: number; // For multi-word query matching
  similarity?: number; // For semantic search
  rank?: number; // For hybrid search
  search_rank?: number; // Alternative ranking property
}

export interface AppCourse extends Omit<DBCourse, 'what_youll_learn'> {
  // Parse what_youll_learn from JSON to string array
  what_youll_learn: string[];
  // Add description as alias for tile_description for compatibility
  description?: string;
  // Course type discriminator for NextGen bar training
  course_type: 'standard' | 'nextgen';
}

export interface AppModule extends Omit<DBModule, 'created_by' | 'description' | 'updated_at'> {
  // Make fields optional for UI state
  created_by: string | null;
  description: string | null;
  updated_at: string | null;
  // Add UI state fields
  lessons?: AppLesson[];
  isExpanded?: boolean;
  isNew?: boolean;
  tempId?: string;
}

export interface AppLesson extends Omit<DBLesson, 'attachments' | 'status' | 'created_by' | 'content' | 'updated_at' | 'video_id'> {
  // Parse attachments from JSON
  attachments?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
  }>;
  // Make status more flexible for UI
  status: string;
  // Make fields optional for UI state
  created_by: string | null;
  content: string | null;
  updated_at: string | null;
  video_id: string | null;
  // Add UI state fields
  isNew?: boolean;
  tempId?: string;
}

// Converter functions
export function dbFlashcardToApp(flashcard: DBFlashcard): AppFlashcard {
  return {
    ...flashcard,
    user_id: flashcard.created_by,
    attachments: undefined, // Will be populated from relationships if needed
    collection: undefined, // Will be populated from relationships if needed
    progress: undefined, // Will be calculated if needed
  };
}

export function dbCaseCollectionToApp(collection: DBCaseCollection): AppCaseCollection {
  return {
    ...collection,
    case_collection_cases: undefined, // Will be populated from relationships
  };
}

export function dbCaseBriefAnalysisToApp(analysis: DBCaseBriefAnalysis): AppCaseBriefAnalysis {
  return {
    ...analysis,
    case_inputs: parseJsonArray(analysis.case_inputs),
    analysis_output: analysis.analysis_output,
  };
}

export function dbCollectionToApp(collection: DBCollection): AppCollection {
  return {
    ...collection,
    subjects: undefined, // Will be populated from relationships
    flashcards_count: undefined, // Will be populated from count query
  };
}

export function dbCaseToApp(dbCase: DBCase): AppCase {
  return {
    ...dbCase,
    tags: parseJsonArray<string>(dbCase.tags),
    nicknames: parseJsonArray<string>(dbCase.nicknames),
    related_cases: parseJsonArray<string>(dbCase.related_cases),
    educational_content: dbCase.educational_content,
    enrichment_metadata: dbCase.enrichment_metadata,
    import_metadata: dbCase.import_metadata,
    html_formats: dbCase.html_formats,
    analysis_metadata: dbCase.analysis_metadata,
  };
}

export function dbCourseToApp(course: DBCourse): AppCourse {
  return {
    ...course,
    what_youll_learn: parseJsonArray<string>(course.what_youll_learn),
    description: course.tile_description,
    course_type: (course.course_type as 'standard' | 'nextgen') || 'standard',
  };
}

export function dbModuleToApp(module: DBModule): AppModule {
  return {
    ...module,
    created_by: module.created_by,
    description: module.description,
    updated_at: module.updated_at,
    lessons: undefined, // Will be populated from relationships
  };
}

export function dbLessonToApp(lesson: DBLesson): AppLesson {
  return {
    ...lesson,
    attachments: parseJsonArray(lesson.attachments),
    status: lesson.status,
    created_by: lesson.created_by,
    content: lesson.content,
    updated_at: lesson.updated_at,
    video_id: lesson.video_id,
  };
}

// Case search result types
export interface CaseSearchResult extends AppCase {
  // Search-specific properties
  title: string; // Usually maps to case_name
  summary: string; // Maps to holding_short or text_snippet
  source: 'library' | 'generated';
  generationTime?: number;
  content?: {
    facts: string;
    issue: string;
    holding: string;
    rule: string;
    reasoning: string;
    application?: string;
    procedural_history?: string;
  };
  metadata?: {
    area_of_law?: string;
    tags?: string[];
    cold_call_questions?: Array<{
      question: string;
      answer: string;
      difficulty?: string;
    }>;
    issue_spotting_questions?: Array<{
      question: string;
      answer: string;
      difficulty?: string;
    }>;
  };
}

export function dbCaseToCaseSearchResult(dbCase: DBCase): CaseSearchResult {
  const appCase = dbCaseToApp(dbCase);
  return {
    ...appCase,
    title: dbCase.case_name || '',
    summary: dbCase.holding_short || dbCase.text_snippet || '',
    source: 'library',
    content: {
      facts: dbCase.facts || '',
      issue: dbCase.issue || '',
      holding: dbCase.holding || '',
      rule: dbCase.rule || '',
      reasoning: dbCase.reasoning || '',
      procedural_history: dbCase.procedural_history,
    },
    metadata: {
      area_of_law: dbCase.subject,
      tags: parseJsonArray<string>(dbCase.tags),
    },
  };
}

// Re-export commonly used types for backward compatibility
export type {
  DBProfile as ProfileRow,
  DBThread as ThreadRow,
  DBMessage as MessageRow,
  DBStripePriceMapping as StripePriceMappingRow,
};

// Export unified case types
export type { CaseSearchResult as UnifiedCaseSearchResult };
export { dbCaseToCaseSearchResult as dbCaseToUnifiedSearchResult };